import { useEffect, useState } from "react";
import DashboardLayout from "../../../components/DashboardLayout";
import RenderToast from "../../../components/RenderToast";
import API from "../../../utils/api";
import classes from "./AddBrand.module.css";

const slugify = (value) =>
  value
    ?.toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-") || "";

export default function AddBrand() {
  const [form, setForm] = useState({ name: "", slug: "" });
  const [saving, setSaving] = useState(false);
  const [brands, setBrands] = useState([]);

  const fetchBrands = async () => {
    try {
      const response = await API.post("/brands.php", { action: "fetch" });
      const list = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];
      setBrands(list);
    } catch {
      RenderToast({ type: "error", message: "Failed to load brands" });
    }
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  const handleNameChange = (value) => {
    setForm({ name: value, slug: slugify(value) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      return RenderToast({
        type: "error",
        message: "Brand name is required",
      });
    }

    const duplicate = brands.find(
      (b) =>
        b.brand_name?.toLowerCase() === form.name.toLowerCase() ||
        b.brand_slug === form.slug
    );

    if (duplicate) {
      return RenderToast({
        type: "error",
        message: "Brand already exists",
      });
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("authToken") || "";
      const params = new URLSearchParams();
      params.append("action", "add");
      params.append("brand_name", form.name.trim());
      params.append("brand_slug", form.slug);

      const response = await fetch(`${API.base}/brands.php`, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params,
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to add brand");
      }

      RenderToast({ type: "success", message: "Brand added successfully" });

      setForm({ name: "", slug: "" });
      fetchBrands();
    } catch (err) {
      RenderToast({ type: "error", message: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className={classes.page}>
        <header className={classes.hero}>
          <p className={classes.kicker}>Catalog</p>
          <h1 className={classes.title}>Add New Brand</h1>
          <p className={classes.subtitle}>
            Create unique brand names — avoid duplicates and maintain a clean
            catalog.
          </p>
        </header>

        <main className={classes.layout}>
          <section className={classes.formCard}>
            <div className={classes.formHeader}>
              <h2>Create Brand</h2>
              <p className={classes.formHint}>
                Brand slug is generated automatically.
              </p>
            </div>

            <form className={classes.form} onSubmit={handleSubmit}>
              <label className={classes.field}>
                <span className={classes.labelText}>Brand Name</span>
                <div className={classes.inputRow}>
                  <input
                    value={form.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="e.g. Samsung"
                    className={classes.input}
                    disabled={saving}
                  />
                  <button
                    type="button"
                    onClick={() => setForm({ name: "", slug: "" })}
                    className={classes.clearBtn}
                  >
                    ✕
                  </button>
                </div>
              </label>

              <label className={classes.field}>
                <span className={classes.labelText}>Slug (Preview)</span>
                <input
                  value={form.slug}
                  disabled
                  className={`${classes.input} ${classes.disabledInput}`}
                />
              </label>

              <div className={classes.actions}>
                <button className={classes.primaryBtn} disabled={saving}>
                  {saving ? "Saving..." : "Add Brand"}
                </button>
                <button
                  type="button"
                  className={classes.ghostBtn}
                  onClick={() => setForm({ name: "", slug: "" })}
                >
                  Reset
                </button>
              </div>
            </form>

            <div className={classes.help}>
              <strong>Tip:</strong> Keep brand names short and recognizable.
            </div>
          </section>
        </main>
      </div>
    </DashboardLayout>
  );
}
